import React, { useEffect, useMemo, useState } from "react";
import { Form, Modal } from "react-bootstrap";

import DstButton from "../../../../../components/DstButton/DstButton";
import MultiSelectDropdown from "../../../../../components/MultiSelectDropdown/MultiSelectDropdown";
import MultiSelectLiveDropdown from "../../../../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import RequiredLabel from "../../../../../components/RequiredLabel/RequiredLabel";
import { CohortIntensity } from "../../../../../models/CohortIntensity";
import { formatToISODateTime } from "../../../../../services/TimeService";
import translate from "../../../../../services/Translate";
import { fetchProjectSubjects, updateProject } from "../../../Project.function";

const EditProjectModal = ({ show, onClose, project, language, onSuccess }: any) => {
  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [overseerLabel, setOverseerLabel] = useState<string>("");
  const [cohortLabel, setCohortLabel] = useState<string>("");

  const [form, setForm] = useState({
    name: "",
    format: "",
    dt_start: "",
    dt_end: "",
    subject: 0,
    overseer_id: null as number | null,
    cohort_id: project.cohort?.id ?? null,
    url: "",
    description: "",
  });

  useEffect(() => {
    fetchProjectSubjects(setSubjects, setLoadingSubjects, language);
  }, [language]);

  useEffect(() => {
    if (!project) return;

    setForm({
      name: project.name,
      format: project.format,
      dt_start: formatToISODateTime(project.dt_start, true).replace(" ", "T"),
      dt_end: formatToISODateTime(project.dt_end, true).replace(" ", "T"),
      subject: project.subject.id,
      overseer_id: project.overseer?.id ?? null,
      cohort_id: project.cohort?.id ?? null,
      url: project.url ?? "",
      description: project.description ?? "",
    });

    setOverseerLabel(project.overseer?.full_name ?? "");
    setCohortLabel(project.cohort?.name ?? "");
  }, [project]);

  const handleChange = (key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    updateProject(
      project.id,
      {
        ...form,
        dt_start: new Date(form.dt_start).toISOString(),
        dt_end: new Date(form.dt_end).toISOString(),
      },
      setLoading,
      () => {
        onSuccess();
        onClose();
      },
      language
    );
  };

  const _selectedSubject =
    subjects.length > 0 ? subjects.find((subject) => subject.id === form.subject) : project?.subject;

  const _formatItems = Object.values(CohortIntensity).map((format) => ({
    id: format,
    name: format,
  }));

  const _isValid = useMemo(
    () => form.name.trim() && form.format && form.dt_start && form.dt_end && form.subject && form.cohort_id,
    [form]
  );

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.EDIT_PROJECT.TITLE")}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              <RequiredLabel label={translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.EDIT_PROJECT.FIELDS.COHORT")} />
            </Form.Label>
            <MultiSelectLiveDropdown
              category={["cohorts"]}
              language={language}
              isSingleSelect
              toggleClass={`w-100 border ${!form.cohort_id ? "text-muted" : ""}`}
              label={
                cohortLabel ||
                translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.EDIT_PROJECT.PLACEHOLDERS.SELECT_COHORT")
              }
              onConfirm={(selected) => {
                const cohort = selected?.[0]?.value ?? null;

                setCohortLabel(cohort ? cohort.name : "");
                setForm((prev) => ({
                  ...prev,
                  cohort_id: cohort?.id ?? null,
                }));
              }}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              <RequiredLabel label={translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.EDIT_PROJECT.FIELDS.SUBJECT")} />
            </Form.Label>
            <MultiSelectDropdown
              key={`subject-${project.id}`}
              items={subjects.map((subject: any) => ({
                id: subject.id,
                name: subject.name,
              }))}
              language={language}
              label={_selectedSubject?.name}
              isSingleSelect
              badgeItemProperty="name"
              displayItem={(item: any) => <span>{item.name}</span>}
              onConfirm={(items: any[]) => {
                if (items.length > 0) {
                  setForm((prev: any) => ({
                    ...prev,
                    subject: items[0].id,
                    name: subjects.find((subject) => subject.id === items[0].id)?.short_name,
                  }));
                }
              }}
              mock={loadingSubjects}
              borderToggle
              btnWidth="100%"
              toggleClass="w-100"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              <RequiredLabel label={translate(language, "GLOBAL.NAME")} />
            </Form.Label>

            <Form.Control value={form.name} onChange={(event) => handleChange("name", event.target.value)} />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{translate(language, "GLOBAL.DESCRIPTION")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.description}
              onChange={(event) => handleChange("description", event.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              <RequiredLabel label={translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.EDIT_PROJECT.FIELDS.FORMAT")} />
            </Form.Label>

            <MultiSelectDropdown
              key={`format-${project.id}`}
              items={_formatItems}
              language={language}
              label={_formatItems.find((format) => format.id === form.format)?.name ?? _formatItems[0]?.name}
              isSingleSelect
              badgeItemProperty="name"
              displayItem={(item: any) => <span>{item.name}</span>}
              onConfirm={(items: any[]) => {
                if (items.length > 0) {
                  setForm((prev: any) => ({
                    ...prev,
                    format: items[0].id,
                  }));
                }
              }}
              borderToggle
              btnWidth="100%"
              toggleClass="w-100"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              <RequiredLabel label={translate(language, "GLOBAL.START_DATE")} />
            </Form.Label>
            <Form.Control
              type="datetime-local"
              value={form.dt_start}
              onChange={(event) => handleChange("dt_start", event.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              <RequiredLabel label={translate(language, "GLOBAL.END_DATE")} />
            </Form.Label>

            <Form.Control
              type="datetime-local"
              value={form.dt_end}
              onChange={(event) => handleChange("dt_end", event.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              <RequiredLabel label={translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.EDIT_PROJECT.FIELDS.MENTOR")} />
            </Form.Label>
            <MultiSelectLiveDropdown
              category={["users"]}
              language={language}
              isSingleSelect
              toggleClass={`w-100 border ${!form.overseer_id ? "text-muted" : ""}`}
              label={
                overseerLabel ||
                translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.EDIT_PROJECT.PLACEHOLDERS.SELECT_MENTOR")
              }
              onConfirm={(selected) => {
                const user = selected?.[0]?.value ?? null;
                setOverseerLabel(user ? `${user.first_name} ${user.last_name}` : "");
                setForm((prev) => ({
                  ...prev,
                  overseer_id: user?.id ?? null,
                }));
              }}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.EDIT_PROJECT.FIELDS.LINK")}</Form.Label>
            <Form.Control value={form.url} onChange={(event) => handleChange("url", event.target.value)} />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <DstButton variant="secondary" value={translate(language, "GLOBAL.CANCEL")} clickFunction={onClose} />

        <DstButton
          loading={loading}
          value={translate(language, "GLOBAL.SAVE")}
          disabled={loadingSubjects || !_isValid}
          clickFunction={handleSubmit}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default EditProjectModal;
